import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal, Button, FormGroup, FormControl, ControlLabel, Alert, ProgressBar } from 'react-bootstrap';

export const ModalSendEmail = (props) => {
    const { t } = useTranslation();

    const [email, setEmail] = useState(props.defaultEmail || '');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sendProgress, setSendProgress] = useState({ current: 0, total: 0, errors: [] });

    const prescriptionCount = props.prescriptions?.length || 1;
    const isMultiple = prescriptionCount > 1;

    const validateEmail = (value) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    };

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        if (error) setError(null);
    };

    const handleSubmit = async () => {
        if (!email) {
            setError(t('email.emailRequired'));
            return;
        }

        if (!validateEmail(email)) {
            setError(t('email.invalidEmail'));
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            if (isMultiple && props.onSendMultiple) {
                const errors = [];
                setSendProgress({ current: 0, total: prescriptionCount, errors: [] });

                for (let i = 0; i < props.prescriptions.length; i++) {
                    try {
                        await props.onSendMultiple({ email }, props.prescriptions[i], i);
                        setSendProgress(prev => ({ ...prev, current: i + 1 }));
                    } catch (err) {
                        errors.push(`${t('email.errorSendingPrescription')} ${i + 1}`);
                        setSendProgress(prev => ({ ...prev, current: i + 1, errors: [...prev.errors, i + 1] }));
                    }
                }

                if (errors.length > 0 && errors.length < prescriptionCount) {
                    setError(t('email.partialSendError', { sent: prescriptionCount - errors.length, total: prescriptionCount }));
                    setIsLoading(false);
                } else if (errors.length === prescriptionCount) {
                    setError(t('email.sendFailed'));
                    setIsLoading(false);
                } else {
                    props.onClose();
                }
            } else {
                await props.onSend({ email });
                props.onClose();
            }
        } catch (err) {
            setError(t('email.sendFailed'));
            setIsLoading(false);
        }
    };

    const handleSkip = () => {
        if (props.onSkip) props.onSkip();
        props.onClose();
    };

    return (
        <Modal show={true} onHide={handleSkip} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{props.title || t('email.sendDocument')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <FormGroup validationState={error ? 'error' : null}>
                    <ControlLabel>{t('email.recipientEmail')}</ControlLabel>
                    <FormControl
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        placeholder={t('email.enterEmail')}
                        disabled={isLoading}
                    />
                    {error && (
                        <span className="help-block" style={{ color: '#a94442' }}>
                            {error}
                        </span>
                    )}
                </FormGroup>

                {isLoading && isMultiple && sendProgress.total > 0 && (
                    <div style={{ marginTop: '15px' }}>
                        <ProgressBar
                            now={(sendProgress.current / sendProgress.total) * 100}
                            label={`${sendProgress.current}/${sendProgress.total}`}
                            striped
                            active
                        />
                    </div>
                )}

                <p style={{ fontSize: '13px', color: '#666', marginTop: '10px' }}>
                    {isMultiple
                        ? t('email.willSendMultipleEmails', { count: prescriptionCount })
                        : t('email.willAttachPdf')
                    }
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle="info" onClick={handleSkip} disabled={isLoading}>
                    {t('buttons.skip')}
                </Button>
                <Button bsStyle="success" onClick={handleSubmit} disabled={isLoading || !email}>
                    {isLoading ? (
                        <><i className="fa fa-spinner fa-spin"></i> {t('buttons.sending')}</>
                    ) : (
                        <><i className="fa fa-envelope"></i> {isMultiple ? t('buttons.sendAll') : t('buttons.send')}</>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

ModalSendEmail.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSend: PropTypes.func.isRequired,
    onSendMultiple: PropTypes.func,
    onSkip: PropTypes.func,
    defaultEmail: PropTypes.string,
    title: PropTypes.string,
    prescriptions: PropTypes.array
};

ModalSendEmail.defaultProps = {
    defaultEmail: '',
    prescriptions: null
};
